import { ItemView, util } from '@glu/core';

import template from './rowSelectorCell.hbs';

/**
 * This ItemView represents a simple row selection checkbox used in conjunction with performing bulk operations.
 * This particular implementation also listens to a "select all" checkbox that resides
 * in the corresponding column header.
 *
 * Note: this implementation is not designed to display boolean values in the underlying model.
 */
export default ItemView.extend({
  template,

  events: {
    'click @ui.rowSelector': 'handleSelectRow'
  },

  ui: {
    rowSelector: '.column-row-selector'
  },

  modelEvents: {
    change: 'updateDisabledState'
  },

  initialize(opts) {
    this.gridCid = opts.gridCid;
    this.initializeEventListeners();
  },

  onVisible() {
    this.updateDisabledState();

    if (this.options.column.has('field') && this.options.column.get('fieldBinding')) {
      const fieldName = this.options.column.get('field');
      this.toggleState(this.$el, this.model.get(fieldName));
    }

    // Render row as selected when re-rendering (e.g. via sorting, filtering or polling)
    if (this.model.gridRowSelected) {
      this.toggleState(this.$el, true);
    }
  },

  updateDisabledState() {
    if (this.isClosed || !this.options.column.has('disabled')) {
      return;
    }

    const disabledFunction = this.options.column.get('disabled');
    const isDisabled = util.isFunction(disabledFunction) ?
      disabledFunction(this.model) :
      disabledFunction;

    this.ui.rowSelector.prop('disabled', isDisabled);
  },

  initializeEventListeners() {
    const allRowsSelectorClass = '.grid-all-rows-selector';
    const groupRowSelector = 'group-row-selector';
    const rowSelectorClass = '.column-row-selector';

    this.listenTo(this, 'render', this.monitorVisibility);

    /**
     * Listen to row selection events.
     * The individual checkboxes are far enough away in the object graph to warrant the use of the
     * appBus to broadcast selection events. This also has the added benefit of only impacting the
     * tableHeader and checkboxCell modules.
     *
     * We are also purposely not modifying the underlying collection.
     * This implementation is designed
     * to only effect those items that are currently visible on that page.
     */
    this.listenTo(this.appBus, `grid:internal:selectAllRows:${this.gridCid}`, e => {
      // Check to see if the "select all" option is selected.
      const isSelected = e.$el.find(allRowsSelectorClass).is(':checked');
      const groupRowSelectors = e.grid.el.getElementsByClassName(groupRowSelector) || [];

      // Set the state of each row, to that of the "select all" option.
      this.$(rowSelectorClass).each((i, el) => {
        this.toggleState(this.$(el).closest('td'), isSelected);
      });

      for (const item of groupRowSelectors) {
        item.checked = isSelected;
      }

      this.appBus.trigger('grid:selectAllRows', isSelected, this.gridCid);
    });

    this.listenTo(this.appBus, `grid:selectAllRows:group:${this.model.get('groupId')}`, this.handleSelectAllGroupRows);
    this.listenTo(this.options.grid, 'grid:contentRendered', this.onVisible);
  },

  handleSelectAllGroupRows(e) {
    const groupRowSelectorClass = '.group-row-selector';
    const rowSelectorClass = '.column-row-selector';

    // Check to see if the "select all group" option is selected.
    const isSelected = e.$el.find(groupRowSelectorClass).is(':checked');

    // Set the state of each row, to that of the "select all" option.
    this.$(rowSelectorClass).each((i, el) => {
      this.toggleState(this.$(el).closest('td'), isSelected);
    });

    this.appBus.trigger('grid:selectRow', this, isSelected);
  },

  handleSelectRow(e) {
    const { checked } = e.currentTarget;
    const fieldName = this.options.column.get('field');

    // Toggle class so that selected rows can be styled
    this.toggleState(this.$el, checked);

    if (fieldName && this.options.column.get('fieldBinding')) {
      this.model.set(fieldName, checked);
    }

    // Broadcast the row select event.
    this.appBus.trigger('grid:selectRow', this, checked);
    this.appBus.trigger(`grid:selectRow:group:${this.model.get('groupId')}`, this, checked);
  },

  toggleState($td, checked) {
    const $tr = $td.closest('tr');
    const $checkbox = $tr.find('> td').eq(this.$el.index()).find(':checkbox');

    if ($checkbox.is(':disabled')) {
      return;
    }

    $tr.toggleClass('selected', checked);
    $checkbox.prop('checked', checked);

    this.model.gridRowSelected = checked;
  },

  templateHelpers() {
    const isChecked = this.options.column.get('checked');
    return {
      checked: typeof isChecked === 'function' ? isChecked(this.options.model) : false,
      id: this.model.cid,
      value: this.options.value
    };
  }
});
